/**
 * 환경/인증 API - campaigns.getEnvironment
 * currentUser, advertisingTypes 등 반환
 *
 * fetch + redirect: 'manual'로 API 302 리다이렉트(/login)를 따라가지 않음.
 * axios는 브라우저에서 리다이렉트를 막을 수 없어 GET /login 로그가 남는 문제가 있음.
 *
 * 동시 다발 호출은 단일 in-flight로 합치고, 짧은 TTL 내 재호출은 네트워크를 생략한다.
 * 로그인 직후 등은 fetchEnvironment({ forceRefresh: true })로 캐시를 우회한다.
 */
import { getNewApiBaseUrl } from './config';
import { readXsrfCookie, ensureCsrfSession } from './csrf';

export interface CurrentUser {
  id: string;
  email?: string;
  name?: string;
  picture?: string;
  avatar?: string;
  permissions?: Record<string, boolean>;
  role: 'admin' | 'advertiser' | 'guest';
  campaignId?: string;
  isAnonymous?: boolean;
  showCaseStudies?: boolean;
}

export interface GetEnvironmentResponse {
  currentUser: CurrentUser;
  advertisingTypes?: unknown[];
  campaignGenres?: unknown[];
  adminsEmails?: string[];
  admins?: Record<string, unknown>;
  templates?: unknown[];
  env?: string;
  organization?: unknown;
}

const GUEST_RESPONSE: GetEnvironmentResponse = {
  currentUser: { id: 'guest', name: 'guest', role: 'guest' },
  advertisingTypes: [],
  campaignGenres: [],
  adminsEmails: [],
  admins: {},
  templates: [],
  env: 'development',
  organization: null,
};

/** 페이지 이동 등 연속 호출 완화 (밀리초) */
const ENV_CACHE_TTL_MS = 30_000;

let inFlight: Promise<GetEnvironmentResponse> | null = null;
let lastSnapshot: { at: number; value: GetEnvironmentResponse } | null = null;

function cloneResponse(v: GetEnvironmentResponse): GetEnvironmentResponse {
  return structuredClone(v);
}

/** 로그아웃 등 다음 로드가 반드시 네트워크를 타게 할 때 */
export function invalidateEnvironmentCache(): void {
  lastSnapshot = null;
}

async function fetchEnvironmentOnce(url: string, useNewEnvelope: boolean):
  Promise<{ ok: true; body: GetEnvironmentResponse } | { ok: false; status: number; guest: boolean }> {
  const doFetch = async () => {
    const token = readXsrfCookie();
    return fetch(url, {
      method: 'POST',
      credentials: 'include',
      redirect: 'manual',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'X-XSRF-TOKEN': token }),
      },
      body: '{}',
    });
  };

  // Heroku의 503 Application Error 페이지처럼 BE가 죽어 있으면 CORS 헤더가 없어서
  // 브라우저가 fetch를 throw 시킨다. 또한 cross-origin 환경에서 네트워크 단절 시도
  // 동일하게 throw. 폴백 로직이 동작하도록 throw를 status=0(추정)로 변환한다.
  let res: Response;
  try {
    res = await doFetch();
  } catch {
    return { ok: false, status: 0, guest: false };
  }

  // 403 CSRF 에러 시 세션 재초기화 후 1회 재시도 (webApi3 인터셉터와 동일 패턴)
  if (res.status === 403) {
    try {
      await ensureCsrfSession(true);
      res = await doFetch();
    } catch {
      return { ok: false, status: 0, guest: false };
    }
  }

  // opaque redirect(302→/login) 또는 명시적 401: 미인증 상태
  if (res.type === 'opaqueredirect' || res.status === 302 || res.status === 401) {
    return { ok: false, status: res.status || 0, guest: true };
  }

  if (!res.ok) {
    return { ok: false, status: res.status, guest: false };
  }

  let body: GetEnvironmentResponse
    | { success: true; data: GetEnvironmentResponse }
    | { success: false; message?: string };
  try {
    body = await res.json();
  } catch {
    return { ok: false, status: res.status, guest: false };
  }

  if (useNewEnvelope) {
    if (body && typeof body === 'object' && 'success' in body) {
      if (body.success === false) {
        // BE rejected (auth/role) — fall back to guest shape.
        return { ok: true, body: GUEST_RESPONSE };
      }
      return { ok: true, body: normalizeNewEnvelope(body.data) };
    }
  }
  return { ok: true, body: body as GetEnvironmentResponse };
}

/**
 * 신규 BE의 admins 응답이 legacy와 형태가 달라서 정규화한다.
 *  - Legacy: `admins: { [adminId]: { profile, email, ... } }`
 *  - New:    `admins: Array<{ id, profile, email, active }>`
 * FE의 `getAdminName(admins, userId) = admins[userId]?.profile?.name` 등
 * 모든 호출처가 record 형태를 가정하므로 배열을 id-keyed record로 변환.
 */
function normalizeNewEnvelope(data: GetEnvironmentResponse): GetEnvironmentResponse {
  const rawAdmins = data?.admins as unknown;
  if (Array.isArray(rawAdmins)) {
    const record: Record<string, unknown> = {};
    for (const admin of rawAdmins as Array<{ id?: string; _id?: string } & Record<string, unknown>>) {
      const key = admin?.id ?? admin?._id;
      if (typeof key === 'string' && key) record[key] = admin;
    }
    return { ...data, admins: record };
  }
  return data;
}

async function fetchEnvironmentFromNetwork(): Promise<GetEnvironmentResponse> {
  // v2-only: POST the new BE environment endpoint. No legacy fallback — a v2
  // failure surfaces (guest on 401/302, throw on real error) instead of being
  // masked by the legacy session.
  const newBase = getNewApiBaseUrl();
  const newRes = await fetchEnvironmentOnce(`${newBase}/admin/campaign/environment/get`, true);
  if (newRes.ok) return newRes.body;
  if (newRes.guest) return GUEST_RESPONSE;
  throw new Error(`getEnvironment failed: ${newRes.status}`);
}

export interface GetEnvironmentOptions {
  forceRefresh?: boolean;
}

export async function getEnvironment(
  options: GetEnvironmentOptions = {},
): Promise<GetEnvironmentResponse> {
  const force = options.forceRefresh === true;
  const now = Date.now();

  if (!force && lastSnapshot && now - lastSnapshot.at < ENV_CACHE_TTL_MS) {
    return cloneResponse(lastSnapshot.value);
  }

  if (inFlight && !force) {
    return inFlight;
  }

  if (inFlight && force) {
    try {
      await inFlight;
    } catch {
      /* 이전 요청 실패는 무시하고 새로 조회 */
    }
    inFlight = null;
    lastSnapshot = null;
  }

  const p = fetchEnvironmentFromNetwork().then((res) => {
    lastSnapshot = { at: Date.now(), value: res };
    return res;
  });
  void p.finally(() => {
    if (inFlight === p) {
      inFlight = null;
    }
  });
  inFlight = p;
  return p;
}
